import React, { useEffect, useMemo, useState } from "react";
import {
	XMarkIcon,
	MinusIcon,
	PlusIcon,
	UserGroupIcon,
} from "@heroicons/react/24/outline";
import { useEventStore } from "../../../store/eventStore";
import { useBusStore } from "../../../store/busStore";

const formatPhone = (hp1, hp2, hp3) => {
	if (!hp1) return "-";
	return [hp1, hp2, hp3].filter(Boolean).join("-");
};

const buildKey = (reservation) =>
	`${reservation.RES_DAY}-${reservation.RES_SEQ}`;

export default function PrepairAssignModal({
	isOpen,
	onClose,
	eventData,
	busService,
	seatmapLayout,
	onPrepare,
}) {
	const { getReservationInfo } = useEventStore();
	const { getSeats } = useBusStore();

	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState(null);
	const [reservationList, setReservationList] = useState([]);
	const [selectedItems, setSelectedItems] = useState([]);
	const [assignCounts, setAssignCounts] = useState({});
	const [assignedCounts, setAssignedCounts] = useState({});

	useEffect(() => {
		if (isOpen && eventData && busService) {
			fetchReservationInfo();
		}
	}, [isOpen, eventData, busService]);

	useEffect(() => {
		if (!isOpen) {
			setReservationList([]);
			setSelectedItems([]);
			setAssignCounts({});
			setAssignedCounts({});
			setError(null);
		}
	}, [isOpen]);

	const fetchReservationInfo = async () => {
		setIsLoading(true);
		setError(null);
		try {
			const [reservationResponse, seatsResponse] = await Promise.all([
				getReservationInfo(eventData.ev_ym, eventData.ev_seq, "go"),
				getSeats(busService.bus_code),
			]);

			const reservations = reservationResponse.success
				? reservationResponse.data
				: [];

			const assignedCountMap = {};
			if (seatsResponse.success) {
				(seatsResponse.data?.items || []).forEach((seat) => {
					if (!seat.assigned_user_id) return;
					if (!assignedCountMap[seat.assigned_user_id]) {
						assignedCountMap[seat.assigned_user_id] = 0;
					}
					assignedCountMap[seat.assigned_user_id] += 1;
				});
			}

			const initialAssignCounts = {};
			reservations.forEach((reservation) => {
				const key = buildKey(reservation);
				const userId = reservation.user_id || `${key}.${eventData.ev_ym}-${eventData.ev_seq}`;
				const total = reservation.TOTAL_CNT || 0;
				const assigned = assignedCountMap[userId] || 0;
				initialAssignCounts[key] = Math.max(0, total - assigned);
			});

			setReservationList(reservations);
			setAssignedCounts(assignedCountMap);
			setAssignCounts(initialAssignCounts);
		} catch (err) {
			console.error("예약 정보 조회 실패:", err);
			setError("예약 정보 조회 중 오류가 발생했습니다.");
		} finally {
			setIsLoading(false);
		}
	};

	const toggleSelect = (key) => {
		setSelectedItems((prev) => {
			if (prev.includes(key)) {
				return prev.filter((item) => item !== key);
			}
			return [...prev, key];
		});
	};

	const updateAssignCount = (key, delta, maxValue) => {
		setAssignCounts((prev) => {
			const current = prev[key] || 0;
			let next = current + delta;
			if (next < 0) next = 0;
			if (Number.isInteger(maxValue) && next > maxValue) next = maxValue;
			return { ...prev, [key]: next };
		});
	};

	const handlePrepare = async () => {
		if (selectedItems.length === 0) {
			alert("배정할 예약을 선택해주세요.");
			return;
		}

		const assignData = reservationList
			.filter((reservation) => selectedItems.includes(buildKey(reservation)))
			.map((reservation) => ({
				id: buildKey(reservation),
				item: reservation,
				assign_count: assignCounts[buildKey(reservation)] || 0,
			}))
			.filter((item) => item.assign_count > 0);

		if (assignData.length === 0) {
			alert("배정 인원을 입력해주세요.");
			return;
		}

		if (typeof onPrepare === "function") {
			await onPrepare({
				eventData,
				busService,
				seatmapLayout,
				assignData,
			});
		} else {
			onClose();
		}
	};

	const totalSelected = useMemo(() => {
		return selectedItems.reduce((sum, key) => sum + (assignCounts[key] || 0), 0);
	}, [selectedItems, assignCounts]);

	if (!isOpen || !eventData || !busService) return null;

	return (
		<div className="fixed inset-0 z-50 overflow-y-auto" aria-modal="true">
			<div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
				<div
					className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
					aria-hidden="true"
					onClick={onClose}
				></div>

				<span
					className="hidden sm:inline-block sm:align-middle sm:h-screen"
					aria-hidden="true"
				>
					&#8203;
				</span>

				<div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full">
					<div className="bg-gray-50 px-4 py-3 border-b border-gray-200 sm:px-6 flex justify-between items-center">
						<div>
							<h3 className="text-lg leading-6 font-medium text-gray-900">
								예약 목록 선택
							</h3>
							<p className="text-sm text-gray-500">
								{eventData.ev_nm} ({eventData.ev_ym}-{eventData.ev_seq})
							</p>
						</div>
						<button
							className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
							onClick={onClose}
						>
							<span className="sr-only">닫기</span>
							<XMarkIcon className="h-6 w-6" aria-hidden="true" />
						</button>
					</div>

					<div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
						<div className="bg-gray-50 px-4 pt-4 pb-2 rounded-md mb-4">
							<div className="grid grid-cols-3 gap-4">
								<div className="flex flex-col">
									<span className="text-xs text-gray-500 mb-1">출발일</span>
									<span className="font-medium text-gray-800">
										{eventData.start_day || "-"}
									</span>
								</div>
								<div className="flex flex-col">
									<span className="text-xs text-gray-500 mb-1">버스 코드</span>
									<span className="font-medium text-gray-800">
										{busService.bus_code}
									</span>
								</div>
								<div className="flex flex-col">
									<span className="text-xs text-gray-500 mb-1">좌석수</span>
									<span className="font-medium text-gray-800">
										{busService.seat_count || 40}석
									</span>
								</div>
							</div>
						</div>

						{error && (
							<div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
								{error}
							</div>
						)}

						{isLoading ? (
							<div className="flex justify-center items-center py-10">
								<div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
							</div>
						) : (
							<div className="border rounded-lg overflow-hidden">
								<table className="min-w-full divide-y divide-gray-200">
									<thead className="bg-gray-50">
										<tr>
											<th className="pl-4 pr-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"></th>
											<th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">예약자</th>
											<th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">연락처</th>
											<th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">총 인원</th>
											<th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">이미 배정</th>
											<th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">배정 인원</th>
										</tr>
									</thead>
									<tbody className="bg-white divide-y divide-gray-200">
										{reservationList.length > 0 ? (
											reservationList.map((reservation) => {
												const key = buildKey(reservation);
												const userId = reservation.user_id || `${key}.${eventData.ev_ym}-${eventData.ev_seq}`;
												const total = reservation.TOTAL_CNT || 0;
												const assigned = assignedCounts[userId] || 0;
												const remaining = Math.max(0, total - assigned);
												const isDisabled = remaining === 0;
												const currentAssign = assignCounts[key] || 0;
												return (
													<tr key={key} className={selectedItems.includes(key) ? "bg-blue-50" : ""}>
														<td className="pl-4 pr-3 py-2">
															<input
																type="checkbox"
																disabled={isDisabled}
																checked={selectedItems.includes(key)}
																onChange={() => toggleSelect(key)}
																className="h-4 w-4 text-blue-600 border-gray-300 rounded"
															/>
														</td>
														<td className="px-3 py-2 text-sm text-gray-900">
															{reservation.RES_NM || "-"}
														</td>
														<td className="px-3 py-2 text-sm text-gray-500">
															{formatPhone(
																reservation.HANDPHONE1,
																reservation.HANDPHONE2,
																reservation.HANDPHONE3
															)}
														</td>
														<td className="px-3 py-2 text-sm text-gray-500">{total}</td>
														<td className="px-3 py-2 text-sm text-gray-500">
															{assigned}
															{isDisabled && (
																<span className="ml-2 text-xs text-green-600">완료</span>
															)}
														</td>
														<td className="px-3 py-2 text-sm text-gray-500">
															<div className="flex items-center gap-2">
																<button
																	onClick={() => updateAssignCount(key, -1, remaining)}
																	disabled={currentAssign <= 0}
																	className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-40"
																>
																	<MinusIcon className="h-4 w-4" />
																</button>
																<span className="min-w-[20px] text-center">{currentAssign}</span>
																<button
																	onClick={() => updateAssignCount(key, 1, remaining)}
																	disabled={currentAssign >= remaining}
																	className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-40"
																>
																	<PlusIcon className="h-4 w-4" />
																</button>
															</div>
														</td>
													</tr>
												);
											})
										) : (
											<tr>
												<td colSpan="6" className="px-4 py-8 text-center text-sm text-gray-500">
													예약 정보가 없습니다.
												</td>
											</tr>
										)}
									</tbody>
								</table>
							</div>
						)}
					</div>

					<div className="bg-gray-50 px-4 py-3 border-t border-gray-200 sm:px-6 flex items-center justify-between">
						<div className="flex items-center text-sm text-gray-600">
							<UserGroupIcon className="h-4 w-4 mr-1" />
							선택 인원: {totalSelected}명
						</div>
						<div className="flex gap-2">
							<button
								onClick={onClose}
								className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
							>
								취소
							</button>
							<button
								onClick={handlePrepare}
								className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700"
							>
								배정 준비 완료
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
